import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner } from '@cowoo/shared/ui-web';
import { ApiProblem, formatDayShort, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { Dialog } from '../../ui';
import { toast } from '../../ui/toast';

/**
 * "Umbuchen" (10-web-admin § 9.2): the booking moves to another free resource
 * of the same kind on the same day, and the member is told (server). The list
 * is what is free *now*; a desk taken between the look and the click comes
 * back as `desk_taken` and the line says so.
 */
export function RebookDialog({
  bookingId,
  kind,
  locationId,
  day,
  blockStart,
  currentResourceId,
  onClose,
}: {
  bookingId: string;
  kind: 'desk' | 'room';
  locationId: string;
  day: string;
  blockStart: number | null;
  currentResourceId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<string | null>(null);
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  const desks = useQuery({
    queryKey: ['admin', 'availability', locationId, day],
    enabled: kind === 'desk',
    queryFn: async () => {
      const { data, error } = await api.GET('/locations/{id}/availability', {
        params: { path: { id: locationId }, query: { day } },
      });
      if (error) throw error;
      return 'desks' in data ? data.desks : [];
    },
  });

  const rooms = useQuery({
    queryKey: ['admin', 'rooms-availability', locationId, day],
    enabled: kind === 'room',
    queryFn: async () => {
      const { data, error } = await api.GET('/locations/{id}/rooms/availability', {
        params: { path: { id: locationId }, query: { day } },
      });
      if (error) throw error;
      return data.rooms;
    },
  });

  const options =
    kind === 'desk'
      ? (desks.data ?? [])
          .filter((desk) => desk.state === 'free' && desk.resourceId !== currentResourceId)
          .map((desk) => ({
            resourceId: desk.resourceId,
            label: [t('admin.bookings.desk', { label: desk.label }), desk.zone].filter(Boolean).join(' · '),
          }))
      : (rooms.data ?? [])
          // A room is a candidate when the booking's own block is free in it.
          .filter(
            (room) =>
              room.resourceId !== currentResourceId &&
              room.blocks.some((block) => block.start === blockStart && block.state === 'free'),
          )
          .map((room) => ({ resourceId: room.resourceId, label: room.label }));

  const loaded = kind === 'desk' ? desks.isFetched : rooms.isFetched;

  const rebook = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST('/admin/bookings/{id}/rebook', {
        params: { path: { id: bookingId } },
        body: { toResourceId: picked! },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast(t('admin.bookings.rebooked'), { tone: 'success' });
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('bookings') });
      onClose();
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  return (
    <Dialog
      title={t('admin.bookings.rebookTitle', { day: formatDayShort(day) })}
      intro={t('admin.bookings.rebookHint')}
      confirmLabel={t('admin.bookings.rebook')}
      onConfirm={() => rebook.mutate()}
      onClose={onClose}
      busy={rebook.isPending}
      disabled={picked === null}
    >
      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}
      {loaded && options.length === 0 ? (
        <p className="text-adminSmall text-muted">{t('admin.bookings.rebookEmpty')}</p>
      ) : (
        <div role="radiogroup" className="flex max-h-[320px] flex-wrap gap-2 overflow-y-auto">
          {options.map((option) => {
            const chosen = picked === option.resourceId;
            return (
              <button
                key={option.resourceId}
                type="button"
                role="radio"
                aria-checked={chosen}
                onClick={() => setPicked(option.resourceId)}
                className={`inline-flex h-8 items-center rounded-full px-3 text-[13px] font-semibold ${
                  chosen ? 'bg-teal text-white' : 'border-line text-ink border bg-white'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
